import type { Space } from "@circle-react/types/Space";
import { useState } from "react";

import type { FetchOptions } from "../helpers/backendRequestHelpers";
import {
  fetchRequest,
  reactQueryPatch,
} from "../helpers/backendRequestHelpers";
import {
  internalApi,
  joinSpacePath,
  spacePath,
  spaceUpdatePath,
} from "../helpers/urlHelpers";

export const useSpaceApi = () => {
  const [isJoined, setIsJoined] = useState(false);
  const [space, setSpace] = useState<Space | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const joinSpace = (spaceId: number) => {
    const data = { space_id: spaceId };

    return fetch(joinSpacePath(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "same-origin",
      body: JSON.stringify(data),
    })
      .then(response => response.json())
      .then(() => {
        setIsJoined(true);
      })
      .catch(error => {
        console.error(error);
      });
  };

  const fetchSpace = (id: number) =>
    fetchRequest<Response>(spacePath(String(id)))
      .then(response => response?.json() as Promise<Space>)
      .then(data => {
        setSpace(data);
      });

  const updateSpace = (
    updates: Partial<Space>,
    spaceId?: number,
    options: FetchOptions = {},
  ) => {
    setIsSubmitting(true);
    const body =
      updates instanceof FormData
        ? updates
        : JSON.stringify({ space: updates });

    const spaceIdToUse = spaceId || space?.id;

    if (!spaceIdToUse) {
      throw new Error("Space ID is required");
    }

    return fetchRequest<Response>(spaceUpdatePath(String(spaceIdToUse)), {
      method: "PATCH",
      body,
      ...options,
    })
      .then(response => response?.json() as Promise<Space>)
      .then(data => {
        setSpace(data);
        return data;
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const maxSpaceLimit = <T = unknown>(spaceGroupId: number) =>
    fetchRequest<T>(internalApi.spaces.maxSpaceLimit(spaceGroupId), {
      method: "GET",
      forReactQuery: true,
    });

  const createSpace = <T = unknown>(attributes: any) => {
    const body = JSON.stringify({
      space: attributes,
    });
    return fetchRequest<T>(internalApi.spaces.create(), {
      method: "POST",
      body,
      forReactQuery: true,
    });
  };

  const reSetWelcomeBannerPreference = (spaceId: number) =>
    reactQueryPatch(
      internalApi.spaces.resetSpaceWelcomeBannerPreferencePath(spaceId),
    );

  return {
    isJoined,
    joinSpace,
    space,
    fetchSpace,
    updateSpace,
    createSpace,
    maxSpaceLimit,
    isSubmitting,
    reSetWelcomeBannerPreference,
  };
};
